// src/components/ModalOverlay.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * ModalOverlay - Componente reutilizable para crear modales con overlay
 * 
 * @param {React.ReactNode} children - Contenido del modal
 * @param {Function} onClose - Función a ejecutar al cerrar el modal
 * @param {Object} style - Estilos personalizados para el overlay
 * @param {Object} containerStyle - Estilos personalizados para el contenedor del modal
 */
const ModalOverlay = ({ children, onClose, style = {}, containerStyle = {} }) => {
  const defaultStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999, // ✅ CAMBIADO: era var(--z-modal)
    backdropFilter: 'blur(2px)',
    animation: 'fadeIn 0.2s ease-in-out',
    overflow: 'auto' // ✅ AGREGADO: permite scroll si el contenido es muy alto
  };

  const defaultContainerStyle = {
    backgroundColor: "var(--white)",
    border: `1px solid var(--gray-300)`,
    borderRadius: "var(--radius-xl)",
    padding: "var(--space-5)",
    width: "90%",
    maxWidth: "500px",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "var(--shadow-xl)",
    animation: 'slideUp 0.3s ease-out',
    position: 'relative',
    margin: 'var(--space-4)' // ✅ AGREGADO: espacio para scroll
  };

  return (
    <>
      {/* Keyframes para animaciones */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* Estilos mejorados para el scrollbar */
        .modal-overlay-container::-webkit-scrollbar {
          width: 8px;
        }
        
        .modal-overlay-container::-webkit-scrollbar-track {
          background: var(--gray-100);
          border-radius: var(--radius-full);
        }
        
        .modal-overlay-container::-webkit-scrollbar-thumb {
          background: var(--gray-400);
          border-radius: var(--radius-full);
        }
        
        .modal-overlay-container::-webkit-scrollbar-thumb:hover {
          background: var(--gray-500);
        }
      `}</style>

      {/* Overlay backdrop */}
      <div 
        style={{ ...defaultStyle, ...style }} 
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        {/* Container del modal */}
        <div 
          className="modal-overlay-container"
          style={{ ...defaultContainerStyle, ...containerStyle }} 
          onClick={e => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </>
  );
};

ModalOverlay.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  style: PropTypes.object,
  containerStyle: PropTypes.object,
};

export default ModalOverlay;